/**
 * Learning hub — Release 2B umbrella page.
 *
 * Tabs:
 *   - Procedures  (renders <Procedures /> inline)
 *   - Training    (renders <TrainingList /> inline)
 *   - Qualifications (renders <QualificationsList /> inline)
 *
 * Tab state is mirrored in the URL query (`?tab=training`) so links from
 * banners / widgets deep-link straight to the right section.
 */
import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { GraduationCap, BookOpen, PlaySquare, Award, Megaphone, Users, ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";
import Procedures from "@/pages/Procedures";
import TrainingList from "@/components/learning/TrainingList";
import QualificationsList from "@/components/learning/QualificationsList";
import AnnouncementsList from "@/components/learning/AnnouncementsList";

const TABS = [
  { key: "announcements",  label: "Announcements",  icon: Megaphone },
  { key: "procedures",     label: "Procedures",     icon: BookOpen },
  { key: "training",       label: "Training",       icon: PlaySquare },
  { key: "qualifications", label: "Qualifications", icon: Award },
];

export default function Learning() {
  const [params, setParams] = useSearchParams();
  const active = useMemo(() => {
    const v = params.get("tab");
    return TABS.some((t) => t.key === v) ? v : "announcements";
  }, [params]);

  function setTab(k) {
    const next = new URLSearchParams(params);
    next.set("tab", k);
    setParams(next, { replace: true });
  }

  return (
    <div className="max-w-6xl mx-auto p-4 space-y-4" data-testid="learning-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-zinc-950 flex items-center gap-2">
            <GraduationCap className="h-6 w-6" /> Learning
          </h1>
          <p className="text-xs text-zinc-500 mt-0.5">
            One place for procedures, training modules, and your professional qualifications.
          </p>
        </div>
        <Link to="/learning/team-development" data-testid="learning-team-development-link">
          <button className="inline-flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs px-3 py-1.5 rounded-md">
            <Users className="h-3.5 w-3.5" /> Team Development
            <ArrowRight className="h-3 w-3" />
          </button>
        </Link>
      </div>

      <div className="border-b border-zinc-200 flex gap-1" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.key}
            role="tab"
            aria-selected={active === t.key}
            onClick={() => setTab(t.key)}
            data-testid={`learning-tab-${t.key}`}
            className={cn(
              "flex items-center gap-1.5 px-3 py-2 text-sm border-b-2 -mb-px transition-colors",
              active === t.key
                ? "border-zinc-950 text-zinc-950 font-medium"
                : "border-transparent text-zinc-500 hover:text-zinc-900",
            )}
          >
            <t.icon className="h-4 w-4" strokeWidth={2} />
            {t.label}
          </button>
        ))}
      </div>

      <div className="pt-2">
        {active === "announcements" && <AnnouncementsList />}
        {active === "procedures" && <Procedures embedded />}
        {active === "training" && <TrainingList />}
        {active === "qualifications" && <QualificationsList />}
      </div>
    </div>
  );
}
